import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import moment from 'moment-timezone';
import airbnbApi from './api';

export const STEP_LABELS = {
  create_property_manager: 'Create property manager',
  set_policies: 'Send cancellation policies',
  set_airbnb_settings: 'Send Airbnb settings',
  enable_airbnb_channel: 'Enable Airbnb channel',
  connect_host_account: 'Connect host account (manual)',
  save_property: 'Save property',
  upload_images: 'Upload images',
  assign_policies: 'Assign policies (optional)',
  enable_airbnb: 'Enable on Airbnb (takeover)',
  check_channel_status: 'Check channel status'
};

// Steps that make sense to run for every property of a PM at once
// Currencies offered for a property's base currency (RedDoorz markets).
export const PROPERTY_CURRENCIES = ['IDR', 'INR', 'PHP', 'VND'];

export const BULK_STEPS =['save_property', 'upload_images', 'assign_policies', 'enable_airbnb', 'check_channel_status'];

export const formatTime = value => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '—');

export const ErrorAlert = ({ error, onClose }) => {
  if (!error) return null;
  return (
    <Alert variant={error.blocked ? 'warning' : 'danger'} dismissible={!!onClose} onClose={onClose}>
      <div className="bld">{error.message}</div>
      {error.reasons && error.reasons.length > 0 && (
        <ul className="mb-0 mt-1">
          {error.reasons.map(reason => <li key={reason}>{reason}</li>)}
        </ul>
      )}
      {error.code && <div className="fnt12 mt-1">Code: {error.code}</div>}
      {error.requestId && <div className="fnt12">NextPax request ID: {error.requestId}</div>}
      {error.invalidFields && (
        <pre className="fnt12 mt-1 mb-0">{JSON.stringify(error.invalidFields, null, 2)}</pre>
      )}
    </Alert>
  );
};

export const Notice = ({ notice, onClose }) =>
  notice ? <Alert variant="success" dismissible onClose={onClose}>{notice}</Alert> : null;

const STATUS_VARIANTS = {
  succeeded: 'success',
  success: 'success',
  failed: 'danger',
  in_progress: 'info',
  pending: 'secondary',
  blocked: 'warning',
  'not needed': 'light'
};

export const StatusBadge = ({ status }) =>
  status
    ? <Badge bg={STATUS_VARIANTS[status] || 'secondary'} text={status === 'not needed' ? 'dark' : undefined}>{status.replace('_', ' ')}</Badge>
    : <span>—</span>;

export const PageLoader = () => (
  <div className="tc pad20"><Spinner animation="border" size="sm" /> Loading…</div>
);

// Master data (NextPax code lists), cached per category+parent for the session
const masterDataCache = {};
export const useMasterData = (category, parentCode) => {
  const key = `${category}|${parentCode || ''}`;
  const [codes, setCodes] = useState(masterDataCache[key] || []);

  useEffect(() => {
    if (!category || (parentCode === '' || parentCode === null)) {
      setCodes([]);
      return;
    }
    if (masterDataCache[key]) {
      setCodes(masterDataCache[key]);
      return;
    }
    let active = true;
    const params = { category };
    if (parentCode) params.parent_code = parentCode;
    airbnbApi.masterData(params)
      .then(list => {
        masterDataCache[key] = list;
        if (active) setCodes(list);
      })
      .catch(() => active && setCodes([]));
    return () => { active = false; };
  }, [category, parentCode, key]);

  return codes;
};

export const clearMasterDataCache = () => {
  Object.keys(masterDataCache).forEach(key => delete masterDataCache[key]);
};

// Some names are long descriptions (e.g. Airbnb policies), so keep options readable
const truncate = (text, max = 70) => (text && text.length > max ? `${text.slice(0, max - 1)}…` : text);
const codeLabel = c => (c.name && c.name !== c.code ? `${truncate(c.name)} (${c.code})` : c.code);

// Dropdown backed by master data. Falls back to a text input when the
// category hasn't been synced yet, so data entry is never blocked.
export const CodeSelect = ({ category, parentCode, value, onChange, required, disabled, placeholder, filter }) => {
  const allCodes = useMasterData(category, parentCode);
  const codes = filter ? allCodes.filter(filter) : allCodes;
  if (allCodes.length === 0) {
    return (
      <Form.Control
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder || `${category} code`}
      />
    );
  }
  return (
    <Form.Select value={value || ''} onChange={e => onChange(e.target.value)} required={required} disabled={disabled}>
      <option value="">{placeholder || 'Select…'}</option>
      {codes.map(c => (
        <option key={`${c.parent_code}-${c.code}`} value={c.code} title={c.name || ''}>{codeLabel(c)}</option>
      ))}
    </Form.Select>
  );
};

export const MultiCodeSelect = ({ category, value, onChange }) => {
  const codes = useMasterData(category);
  const selected = value || [];
  if (codes.length === 0) {
    return (
      <Form.Control
        value={selected.join(', ')}
        onChange={e => onChange(e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
        placeholder="Comma separated codes"
      />
    );
  }
  return (
    <Form.Select
      multiple
      htmlSize={5}
      value={selected}
      onChange={e => onChange(Array.from(e.target.selectedOptions).map(o => o.value))}
    >
      {codes.map(c => <option key={c.code} value={c.code}>{codeLabel(c)}</option>)}
    </Form.Select>
  );
};

// Flow A step table: status, blockers, last result and a Run button per step.
// subjectPath is `property_managers/:id` or `properties/:id`.
export const FlowASteps = ({ subjectPath, bulkPmId, onChange, refreshKey }) => {
  const [steps, setSteps] = useState(null);
  const [running, setRunning] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);

  const load = useCallback(() => {
    airbnbApi.flowSteps(subjectPath)
      .then(data => setSteps(data.steps))
      .catch(setError);
  }, [subjectPath]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const finish = () => {
    setRunning(null);
    load();
    if (onChange) onChange();
  };

  const run = step => {
    if (step === 'connect_host_account'
      && !window.confirm('Mark the host account as connected? Only do this after the host accepted NextPax on airbnb.com.')) {
      return;
    }
    setRunning(step);
    setError(null);
    setNotice(null);
    airbnbApi.runStep(subjectPath, step)
      .then(result => {
        const warnings = (result.warnings || []).map(w => (typeof w === 'string' ? w : w.message)).filter(Boolean);
        setNotice(`${STEP_LABELS[step] || step} succeeded${result.request_id ? ` (request ${result.request_id})` : ''}`
          + (warnings.length ? ` — warnings: ${warnings.join('; ')}` : ''));
      })
      .catch(setError)
      .finally(finish);
  };

  const bulkRun = step => {
    if (!window.confirm(`Run "${STEP_LABELS[step]}" for every property of this property manager?`)) return;
    setRunning(`bulk:${step}`);
    setError(null);
    setNotice(null);
    airbnbApi.bulkRunStep(bulkPmId, step)
      .then(setBulkResults)
      .catch(setError)
      .finally(finish);
  };

  if (!steps) return error ? <ErrorAlert error={error} /> : <PageLoader />;

  return (
    <>
      <ErrorAlert error={error} onClose={() => setError(null)} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <div className="table-responsive">
        <Table bordered size="sm" className="bg1 align-middle">
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              <th>Step</th>
              <th>Status</th>
              <th>Last run</th>
              <th>Details</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {steps.map((s, index) => (
              <tr key={s.step}>
                <td>{index + 1}</td>
                <td>{STEP_LABELS[s.step] || s.step}</td>
                <td><StatusBadge status={s.not_needed ? 'not needed' : (s.status || (s.blocked ? 'blocked' : null))} /></td>
                <td className="fnt12">{formatTime(s.performed_at)}</td>
                <td className="fnt12">
                  {s.not_needed && <span className="text-muted">Optional — {s.not_needed}</span>}
                  {s.blocked && !s.not_needed && (
                    <ul className="mb-0 ps-3 color7">
                      {s.blockers.map(b => <li key={b}>{b}</li>)}
                    </ul>
                  )}
                  {s.status === 'failed' && (
                    <div className="text-danger">{s.error_code ? `${s.error_code}: ` : ''}{s.error_message}</div>
                  )}
                  {s.request_id && <div className="text-muted">Request {s.request_id}</div>}
                </td>
                <td>
                  {!s.not_needed && <Button size="sm" variant={s.status === 'succeeded' ? 'outline-primary' : 'primary'}
                    disabled={!!running || s.blocked} onClick={() => run(s.step)}>
                    {running === s.step ? <Spinner animation="border" size="sm" /> : (s.status ? 'Re-run' : 'Run')}
                  </Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {bulkPmId && (
        <div className="margtop10">
          <div className="bld margbot5">Run a property step for all properties</div>
          {BULK_STEPS.map(step => (
            <Button key={step} size="sm" variant="outline-secondary" className="me-2 mb-2"
              disabled={!!running} onClick={() => bulkRun(step)}>
              {running === `bulk:${step}` ? <Spinner animation="border" size="sm" /> : STEP_LABELS[step]}
            </Button>
          ))}
          {bulkResults && (
            <div className="table-responsive">
              <Table bordered size="sm" className="bg1 margtop10">
                <thead><tr><th>Property ID</th><th>Status</th><th>Details</th></tr></thead>
                <tbody>
                  {bulkResults.map(r => (
                    <tr key={r.property_id}>
                      <td>{r.property_id}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="fnt12">
                        {r.reasons && r.reasons.join('; ')}
                        {r.message && `${r.code ? `${r.code}: ` : ''}${r.message}`}
                        {r.request_id && `Request ${r.request_id}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
    </>
  );
};
